<template>
    <div :class="[isMobile ? 'ans-facet-full' : 'ans-facet-sidebar', 'terminology-filter-facet-container']">
        <!-- Facet Filter Bloc -->
        <div v-for="facetConfig in conceptsStore.facetsList.filter(f => f.terminologyFacets)"
            :key="facetConfig.terminoName" class="panel panel-default facet-bloc">
            <div class="panel-heading">
                <h6 class="panel-title">
                    <strong>{{ facetConfig.label }}</strong>
                </h6>
            </div>
            <div class="panel-body">
                <div class="text-center">
                    <small class="text-muted">{{ facetConfig.docCount }}</small>
                </div>
                <div>
                    <button class="check-uncheck-btn btn btn-link btn-sm" type="button"
                        @click="checkOrUncheckAllAttributes(facetConfig.terminoName)">
                        {{ getCheckUncheckButtonText(facetConfig.terminoName) }}
                    </button>
                </div>
                <div v-for="terminologyFacet in facetConfig.terminologyFacets"
                    :key="`${facetConfig.terminoName}-${terminologyFacet.nameFr}-${terminologyFacet.nameEn}`"
                    class="facet-entry">
                    <!-- Entry header with expand button -->
                    <div class="facet-entry-header">

                        <button class="dropdown-toggle-btn" type="button" @click="toggleDropdown(terminologyFacet)"
                            :aria-expanded="terminologyFacet.expanded">
                            <svg class="svg-icon svg-slider-right" :class="{ 'rotated': terminologyFacet.expanded }"
                                aria-hidden="true" focusable="false">
                                <use
                                    xlink:href="/wp-content/themes/ans/design-system/svg-icons/icon-sprite.svg#slider-right">
                                </use>
                            </svg>
                        </button>


                        <!-- Check/Uncheck all repartitions checkbox -->
                        <div v-if="terminologyFacet.terminologySpecificFacetValues && terminologyFacet.terminologySpecificFacetValues.length > 0"
                            class="check-all-repartitions-inline">
                            <div class="custom-control custom-checkbox">
                                <input type="checkbox"
                                    :id="`facet-${facetConfig.terminoName}-${terminologyFacet.nameFr}-all`"
                                    class="custom-control-input facet-entry-input" :checked="terminologyFacet.checked"
                                    @change="toggleAllRepartitions(terminologyFacet)"
                                    :aria-label="`Sélectionner toutes les sous-répartitions de ${getFacetEntryName(terminologyFacet)}`">
                                <label :for="`facet-${facetConfig.terminoName}-${terminologyFacet.nameFr}-all`"
                                    class="custom-control-label check-all-label"
                                    :title="`Sélectionner toutes les sous-répartitions`">
                                </label>
                            </div>
                        </div>

                        <div class="entry-title">
                            <span class="label-text">{{ getFacetEntryName(terminologyFacet) }}</span>
                            <span class="counter">{{ getFacetCount(terminologyFacet) }}</span>
                        </div>
                    </div>

                    <!-- Repartitions dropdown content -->
                    <div v-show="terminologyFacet.terminologySpecificFacetValues && terminologyFacet.terminologySpecificFacetValues.length > 0 && terminologyFacet.expanded"
                        class="repartitions-list">
                        <div v-for="(repartition, rIndex) in getSortedRepartitions(terminologyFacet.terminologySpecificFacetValues)"
                            :key="`${facetConfig.terminoName}-${terminologyFacet.nameFr}-${repartition.label}`"
                            class="repartition-item ml-5">
                            <div class="custom-control custom-checkbox">
                                <input type="checkbox"
                                    :id="`facet-${facetConfig.terminoName}-${terminologyFacet.nameFr}-${rIndex}`"
                                    class="custom-control-input facet-repartition-entry-input"
                                    :value="repartition.label" :checked="repartition.checked"
                                    @change="toggleRepartitionSelection(terminologyFacet, repartition)">
                                <label :for="`facet-${facetConfig.terminoName}-${terminologyFacet.nameFr}-${rIndex}`"
                                    class="custom-control-label">
                                    <span class="label-text">{{ repartition.label }}</span>
                                    <span class="counter">{{ repartition.docCount }}</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import FacetStore from "../facet-store";
import ConceptsStore from "../concepts-store";

export default {
    name: "TerminologyFilterFacet",

    data() {
        return {
            facetsList: ConceptsStore.state.facetsList,
            facetStore: FacetStore.state,
            conceptsStore: ConceptsStore.state,
            selectedFacets: {}, // Structure: { terminologyId: { entryField: [repartition1, repartition2] } }
            buttonStates: {}, // Track button state per terminology (true = all checked)
        }
    },

    mounted() {
        // Initialize all attributes as checked by default
        this.initializeAllAttributesAsChecked();
        // Emit event to notify parent that this component is ready
        this.$emit('component-mounted');
    },

    computed: {
        /**
         * Vérifie si l'affichage est sur un appareil mobile
         * @returns {boolean} True si l'écran est inférieur à 768px, false sinon
         */
        isMobile() {
            return window.innerWidth < 768;
        }
    },

    methods: {
        /**
         * Récupère le nom de l'entrée de facette en fonction de la langue
         * @param {Object} entry - Entrée de facette
         * @returns {string} Nom de l'entrée
         */
        getFacetEntryName(entry) {
            // Utiliser la langue de l'interface (français par défaut)
            const lang = this.$route?.query?.lang || 'fr';
            return lang === 'fr' ? entry.nameFr : entry.nameEn;
        },


        /**
         * Récupère le nombre total de concepts pour une entrée de facette
         * @param entry 
         */
        getFacetCount(entry) {
            let totalCount = 0;
            for (const value of entry.terminologySpecificFacetValues) {
                totalCount += value.docCount || 0;
            }
            return totalCount;
        },



        /**
         * Récupère le nombre de concepts pour une terminologie
         * @param {Object} facetConfig - Configuration de la facette
         * @returns {string} Texte affichant le nombre de concepts
         */
        getConceptCount(facetConfig) {
            const terminology = this.conceptsStore.facets.options.all
                .find(t => t.id === facetConfig.terminologyId);

            if (!terminology || !terminology.docCount) {
                return '';
            }

            const count = terminology.docCount;
            const lang = this.$route?.query?.lang || 'fr';

            if (lang === 'fr') {
                return `${count} concept${count > 1 ? 's' : ''} trouvé${count > 1 ? 's' : ''}`;
            } else {
                return `${count} concept${count > 1 ? 's' : ''} found`;
            }
        },

        /**
         * Initialise tous les attributs comme cochés par défaut
         */
        initializeAllAttributesAsChecked() {
            // Émettre l'événement pour notifier le parent
            this.$emit('terminologie-facets-changed');
        },

        /**
         * Retourne les répartitions triées par label
         * @param {Array} terminologySpecificFacetValues - Tableau des répartitions spécifiques à la terminologie
         * @returns {Array} Tableau des répartitions triées
         */
        getSortedRepartitions(terminologySpecificFacetValues) {
            if (!terminologySpecificFacetValues || !Array.isArray(terminologySpecificFacetValues)) {
                return [];
            }
            return [...terminologySpecificFacetValues].sort((a, b) => {
                const labelA = (a.label || '').toLowerCase();
                const labelB = (b.label || '').toLowerCase();
                return labelA.localeCompare(labelB);
            });
        },

        /**
         * Récupère le texte du bouton check/uncheck pour une terminologie
         * @param {string} terminologyId - ID de la terminologie
         * @returns {string} Le texte du bouton
         */
        getCheckUncheckButtonText(terminologyId) {
            // Use buttonStates to determine text, defaults to true (all checked)
            const isAllChecked = this.buttonStates[terminologyId] !== false;
            return isAllChecked ? "Tout désélectionner" : "Tout sélectionner";
        },

        /**
         * Coche tous les attributs d'une entree specifique
         * @param {string} terminologyId - ID de la terminologie
         * @param {Object} entry - Entree d'attribut
         * @private
         */
        checkAllAttributes(terminologyId, entry) {
            // Update checked state for the specific entry and its repartitions
            this.$set(entry, 'checked', true);

            if (entry.repartitions && entry.repartitions.length > 0) {
                for (const repartition of entry.repartitions) {
                    this.$set(repartition, 'checked', true);
                }
            }

            // Add all repartitions to selectedFacets
            if (!this.selectedFacets[terminologyId]) {
                this.$set(this.selectedFacets, terminologyId, {});
            }
            if (entry.repartitions && entry.repartitions.length > 0) {
                this.$set(this.selectedFacets[terminologyId], entry.field, [...entry.repartitions]);
            }
        },

        /**
         * Décoche tous les attributs d'une entrée spécifique
         * @param {string} terminologyId - ID de la terminologie
         * @param {Object} entry - Entrée d'attribut
         * @private
         */
        uncheckAllAttributes(terminologyId, entry) {
            // Update checked state for the specific entry and its repartitions
            this.$set(entry, 'checked', false);

            if (entry.repartitions && entry.repartitions.length > 0) {
                for (const repartition of entry.repartitions) {
                    this.$set(repartition, 'checked', false);
                }
            }

            // Remove the entry from selectedFacets
            if (this.selectedFacets[terminologyId]) {
                this.$delete(this.selectedFacets[terminologyId], entry.field);
                if (Object.keys(this.selectedFacets[terminologyId]).length === 0) {
                    this.$delete(this.selectedFacets, terminologyId);
                }
            }
        },

        /**
         * Coche ou décoche tous les selections d'une terminologie
         * @param {string} terminologyId - ID de la terminologie
         */
        checkOrUncheckAllAttributes(terminologyId) {

            // Toggle button state
            const currentState = this.buttonStates[terminologyId] !== false;
            const newState = !currentState;
            this.$set(this.buttonStates, terminologyId, newState);


            // find the facet configuration for the given terminologyId from ConceptsStore.facetsList
            for (const config of this.conceptsStore.facetsList) {
                if (config.terminoName === terminologyId) {
                    if (config.terminologyFacets) {
                        // Apply to all entries in the terminology
                        for (const entry of config.terminologyFacets) {
                            this.$set(entry, 'checked', newState);
                            if (entry.terminologySpecificFacetValues) {
                                for (const repartition of entry.terminologySpecificFacetValues) {
                                    this.$set(repartition, 'checked', newState);
                                }
                            }
                        }
                    }
                    break;
                }
            }

            // Force component update
            this.$forceUpdate();

            // Émettre l'événement pour notifier le parent
            this.$emit('terminologie-facets-changed');
        },

        /**
         * Bascule la sélection d'une répartition
         * @param {Object} terminologyFacet - Entrée de facette parent
         * @param {Object} repartition - Répartition à basculer
         */
        toggleRepartitionSelection(terminologyFacet, repartition) {
            this.$set(repartition, 'checked', !repartition.checked);

            if (terminologyFacet && Array.isArray(terminologyFacet.terminologySpecificFacetValues)) {
                const hasCheckedRepartition = terminologyFacet.terminologySpecificFacetValues
                    .some(value => value.checked);
                this.$set(terminologyFacet, 'checked', hasCheckedRepartition);
            }

            // Force component update
            this.$forceUpdate();
            this.$emit('terminologie-facets-changed');
        },

        /**
         * Bascule l'état d'ouverture/fermeture d'un dropdown
         * @param {string} terminologyId - ID de la terminologie
         * @param {string} field - Champ de l'entrée
         */
        toggleDropdown(terminologyFacet) {
            this.$set(terminologyFacet, 'expanded', !terminologyFacet.expanded);
            // Force component update
            this.$forceUpdate();
        },

        /**
         * Vérifie si un dropdown est ouvert
         * @param {string} terminologyId - ID de la terminologie
         * @param {string} field - Champ de l'entrée
         * @returns {boolean} True si le dropdown est ouvert
         */
        isDropdownOpen(terminologyFacet) {
            return terminologyFacet.expanded;
        },

        /**
         * Coche toutes les répartitions d'une entrée
         * @param {string} terminologyId - ID de la terminologie
         * @param {Object} entry - Entrée d'attribut
         * @private
         */
        checkAllRepartitions(terminologyId, entry) {
            // Ensure the terminologyId exists in selectedFacets
            if (!this.selectedFacets[terminologyId]) {
                this.$set(this.selectedFacets, terminologyId, {});
            }

            this.$set(this.selectedFacets[terminologyId], entry.field, [...entry.repartitions]);
            this.$set(entry, 'checked', true);

            if (entry.terminologySpecificFacetValues) {
                for (const repartition of entry.terminologySpecificFacetValues) {
                    this.$set(repartition, 'checked', true);
                }
            }
        },

        /**
         * Décoche toutes les répartitions d'une entrée
         * @param {string} terminologyId - ID de la terminologie
         * @param {Object} entry - Entrée d'attribut
         * @private
         */
        uncheckAllRepartitions(terminologyId, entry) {
            // Uncheck the entry
            this.$set(entry, 'checked', false);

            // Uncheck all repartitions for this specific entry only
            if (entry.repartitions) {
                for (const repartition of entry.repartitions) {
                    this.$set(repartition, 'checked', false);
                }
            }

            // Remove this specific entry from selectedFacets
            if (this.selectedFacets[terminologyId]) {
                this.$delete(this.selectedFacets[terminologyId], entry.field);

                // If no more entries for this terminology, remove the terminology
                if (Object.keys(this.selectedFacets[terminologyId]).length === 0) {
                    this.$delete(this.selectedFacets, terminologyId);
                }
            }
        },

        /**
         * Bascule la sélection de toutes les répartitions d'une entrée
         * @param {Object} terminologyFacet - Entrée d'attribut
         */
        toggleAllRepartitions(terminologyFacet) {
            let newCheckedState = !terminologyFacet.checked;
            this.$set(terminologyFacet, 'checked', newCheckedState);
            if (terminologyFacet.terminologySpecificFacetValues !== undefined) {
                for (const repartition of terminologyFacet.terminologySpecificFacetValues) {
                    this.$set(repartition, 'checked', newCheckedState);
                }
            }

            // Force component update
            this.$forceUpdate();

            // Émettre l'événement pour notifier le parent
            this.$emit('terminologie-facets-changed');
        }
    }
}
</script>

<style lang="scss" scoped>
.ans-facet-sidebar {
    margin-top: 20px;
}

.ans-facet-full {
    position: fixed;
    width: 95%;
    z-index: 9999;
    left: 50%;
    top: 50%;
    transform: translateX(-50%) translateY(-50%);
}

.facet-bloc {
    margin-bottom: 20px;
    background-color: #FFFFFF;
    border: 1px solid #e0e0e0;
    border-radius: 4px;

    &:last-child {
        margin-bottom: 0;
    }

    .panel-heading {
        background-color: #1D71B8;
        border-bottom: 1px solid #dee2e6;
        text-align: center;
        padding: 15px 10px;

        .panel-title {
            color: #FFFFFF;
            margin: 0;
            font-size: 18px;
        }
    }

    .panel-body {
        max-height: 30rem;
        overflow: auto;
        padding: 10px;
        background-color: #FFFFFF;
    }
}



.facet-entry-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.entry-title {
    flex: 1;
    font-weight: 500;
    color: #333;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;

    .label-text {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
        word-wrap: break-word;
        padding-right: 8px;
    }

    .counter {
        flex-shrink: 0;
        font-size: 12px;
        color: rgba(0, 0, 0, 0.6);
        font-weight: normal;
        background-color: rgba(255, 255, 255, 0.9);
        padding: 2px 6px;
        border-radius: 3px;
        min-width: 30px;
        text-align: center;
        line-height: 1.2;
        white-space: nowrap;
    }
}

.dropdown-toggle-placeholder {
    min-width: 24px;
    padding: 0.25rem 0.5rem;
    flex-shrink: 0;
}

.dropdown-toggle-btn {
    background: none;
    border: none;
    padding: 0.25rem 0.5rem;
    cursor: pointer;
    color: var(--primary);
    font-size: 1rem;
    min-width: 24px;
    text-align: center;
    flex-shrink: 0;

    &:hover {
        color: var(--primary);
        opacity: 0.8;

        .svg-icon {
            fill: var(--primary);
            opacity: 0.8;
        }
    }

    &:focus {
        outline: none;
    }

    .svg-icon {
        width: 16px;
        height: 16px;
        fill: var(--primary);
        transition: transform 0.3s ease;
        display: block;

        &.rotated {
            transform: rotate(90deg);
        }
    }
}

.repartitions-list {
    padding-left: 1.75rem;
}

.check-all-repartitions-inline {
    display: flex;
    align-items: center;
    margin: 0 0.5rem;

    .custom-control {
        margin: 0;
    }

    .check-all-label {
        margin: 0;
        min-height: auto;
        cursor: pointer;

        &:hover::before {
            border-color: #1D71B8;
        }
    }
}

.facet-entry:has(.facet-repartition-entry-input:checked):has(.facet-repartition-entry-input:not(:checked)) .facet-entry-input+.check-all-label::before {
    border-color: #1D71B8;
    background-color: #1D71B8;
}

.facet-entry:has(.facet-repartition-entry-input:checked):has(.facet-repartition-entry-input:not(:checked)) .facet-entry-input+.check-all-label::after {
    background-image: none !important;
    content: "_" !important;
    color: #FFFFFF;
    font-weight: 700;
    font-size: 14px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    transform: translateY(-6px);
}

.repartition-item {
    margin-bottom: 0.5rem;

    &:last-child {
        margin-bottom: 0;
    }
}

.custom-control {
    display: flex;
    align-items: flex-start;
}

.custom-control-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    cursor: pointer;
    min-height: 24px;

    &:hover {
        color: var(--primary);
    }

    .label-text {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
        word-wrap: break-word;
        padding-right: 8px;
    }

    .counter {
        flex-shrink: 0;
        font-size: 12px;
        color: rgba(0, 0, 0, 0.6);
        font-weight: normal;
        background-color: rgba(255, 255, 255, 0.9);
        padding: 2px 6px;
        border-radius: 3px;
        min-width: 30px;
        text-align: center;
        line-height: 1.2;
        white-space: nowrap;
    }
}

/* Responsive adjustments */
@media (max-width: 767.98px) {
    .terminology-filter-facet-container {
        width: 100%;
        position: static;
    }

    .ans-facet-full {
        width: 100%;
        position: static;
        transform: none;
        left: auto;
        top: auto;
        z-index: auto;
        overflow-y: visible;
    }

    .panel-body {
        padding: 1rem 0.75rem;
        max-height: none;
        overflow-y: visible;
    }
}

@media (min-width: 768px) and (max-width: 991.98px) {
    .ans-facet-sidebar {
        position: static;
        margin-bottom: 1rem;
    }

    .panel-body {
        max-height: none;
        overflow-y: visible;
    }
}
</style>
